import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addDaysToDateInput, todayDateInput } from '../../constants/fitnessChallengeTemplates';
import {
	activityNeedsCustomText,
	buildActivityCustomParams,
	unitNeedsCustomText,
	COMMUNITY_CHALLENGE_MODES,
	defaultCustomSelection,
	featuredCommunityTemplates,
	findCommunityTemplate,
	resolveCommunityTemplateKey
} from '../../constants/communityChallengeTemplates';
import {
	createCommunityChallenge,
	fetchCommunityEligibleProfiles,
	fetchCommunityTemplates
} from '../../utils/communityChallengeApi';
import CommunityAudiencePicker from './CommunityAudiencePicker';
import CommunityChallengePostSummary from './CommunityChallengePostSummary';
import PostCommunityChallengeConfigurator from './PostCommunityChallengeConfigurator';
import { fieldLabelClass, inputClass } from './challengeWireFormStyles';

export default function PostCommunityChallengeSheet({
	isOpen,
	onClose,
	user,
	csrfToken,
	endpoints,
	profiles = [],
	onPosted
}) {
	const defaults = defaultCustomSelection();
	const [mode, setMode] = useState(COMMUNITY_CHALLENGE_MODES.CUSTOM);
	const [activityId, setActivityId] = useState(defaults.activityId);
	const [mechanic, setMechanic] = useState(defaults.mechanic);
	const [unitId, setUnitId] = useState(defaults.unitId);
	const [customActivityText, setCustomActivityText] = useState('');
	const [customUnitText, setCustomUnitText] = useState('');
	const [featuredKey, setFeaturedKey] = useState('master_the_mile');
	const [templateCatalog, setTemplateCatalog] = useState([]);
	const [wizardRules, setWizardRules] = useState(null);
	const [durationDays, setDurationDays] = useState(7);
	const [startsOn, setStartsOn] = useState(() => todayDateInput());
	const [title, setTitle] = useState('');
	const [description, setDescription] = useState('');
	const [networkAccounts, setNetworkAccounts] = useState([]);
	const [audienceMode, setAudienceMode] = useState('full_network');
	const [invitedUserIds, setInvitedUserIds] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const modalRef = useRef(null);

	const templateKey = useMemo(
		() =>
			resolveCommunityTemplateKey({
				mode,
				mechanic,
				activityId,
				unitId,
				featuredKey,
				catalog: templateCatalog,
				wizard: wizardRules
			}),
		[mode, mechanic, activityId, unitId, featuredKey, templateCatalog, wizardRules]
	);

	const template = useMemo(
		() => findCommunityTemplate(templateKey, templateCatalog),
		[templateKey, templateCatalog]
	);
	const needsCustomActivity = activityNeedsCustomText(activityId);
	const needsCustomUnit = unitNeedsCustomText(unitId);
	const endsOn = useMemo(
		() => addDaysToDateInput(startsOn, Math.max(1, durationDays)),
		[startsOn, durationDays]
	);

	useEffect(() => {
		if (!isOpen) return undefined;

		const defaults = defaultCustomSelection();
		setMode(COMMUNITY_CHALLENGE_MODES.CUSTOM);
		setActivityId(defaults.activityId);
		setMechanic(defaults.mechanic);
		setUnitId(defaults.unitId);
		setCustomActivityText('');
		setCustomUnitText('');
		setTitle('');
		setDescription('');
		setError('');
		setDurationDays(7);
		setStartsOn(todayDateInput());
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || !user?.id) return undefined;

		let cancelled = false;
		Promise.all([
			fetchCommunityEligibleProfiles({
				userId: user.id,
				endpoint: endpoints.get_user_community_challenge_eligible_profiles_endpoint,
				csrfToken
			}),
			fetchCommunityTemplates({
				userId: user.id,
				endpoint: endpoints.get_user_community_challenge_templates_endpoint,
				csrfToken
			})
		])
			.then(([profilesData, templatesData]) => {
				if (cancelled) return;
				const accounts = profilesData?.accounts || [];
				const catalog = templatesData?.templates || [];
				setNetworkAccounts(accounts);
				setTemplateCatalog(catalog);
				setWizardRules(templatesData?.wizard || null);
				setAudienceMode('full_network');
				setInvitedUserIds(accounts.map((account) => account.user_id));
				const featured = featuredCommunityTemplates(catalog);
				if (featured[0]?.key) setFeaturedKey(featured[0].key);
			})
			.catch(() => {
				if (cancelled) return;
				setNetworkAccounts([]);
				setTemplateCatalog([]);
				setWizardRules(null);
				setInvitedUserIds([]);
			});

		return () => {
			cancelled = true;
		};
	}, [isOpen, user?.id, endpoints, csrfToken]);

	useEffect(() => {
		if (!isOpen || !template?.default_duration_days) return;
		setDurationDays(template.default_duration_days);
	}, [isOpen, templateKey, template?.default_duration_days]);

	useEffect(() => {
		function handleClickOutside(event) {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose();
			}
		}
		if (isOpen) {
			document.addEventListener('click', handleClickOutside);
		}
		return () => document.removeEventListener('click', handleClickOutside);
	}, [isOpen, onClose]);

	const isCustomMode = mode === COMMUNITY_CHALLENGE_MODES.CUSTOM;

	const creatorName = useMemo(() => {
		const primaryProfile = profiles.find((p) => p.is_primary) || profiles[0];
		return primaryProfile?.name || user?.full_name || user?.first_name || null;
	}, [profiles, user]);

	const previewTitle = useMemo(() => {
		if (isCustomMode) return title.trim();
		return findCommunityTemplate(featuredKey, templateCatalog)?.label || '';
	}, [isCustomMode, title, featuredKey, templateCatalog]);

	function postValidationError() {
		if (templateCatalog.length === 0 || !wizardRules) return 'Loading challenge templates…';
		if (!isCustomMode) return null;
		if (!title.trim()) return 'Add a challenge title to post.';
		if (!activityId) return 'Select what you are doing.';
		if (!mechanic) return 'Select how you win.';
		if (!unitId) return 'Select a unit of measurement.';
		if (needsCustomActivity && !customActivityText.trim()) {
			return 'Describe the activity to post.';
		}
		if (needsCustomUnit && !customUnitText.trim()) {
			return 'Describe the unit of measurement to post.';
		}
		return null;
	}

	if (!isOpen) return null;

	function handlePostClick() {
		const validationError = postValidationError();
		if (validationError) {
			setError(validationError);
			return;
		}
		handlePost();
	}

	async function handlePost() {
		setLoading(true);
		setError('');
		try {
			const primaryProfile = profiles.find((p) => p.is_primary) || profiles[0];
			const created = await createCommunityChallenge({
				userId: user.id,
				endpoint: endpoints.create_user_community_challenge_endpoint,
				csrfToken,
				payload: {
					template_key: templateKey,
					title: isCustomMode ? title.trim() : findCommunityTemplate(templateKey, templateCatalog)?.label,
					starts_on: startsOn,
					ends_on: endsOn,
					duration_days: durationDays,
					description: description.trim() || undefined,
					creator_fitness_profile_id: primaryProfile?.id,
					audience_mode: audienceMode,
					...(isCustomMode
						? {
								custom_params: buildActivityCustomParams({
									activityId,
									customActivityText,
									mechanic,
									unitId,
									customUnitText,
									templateKey
								})
							}
						: {}),
					...(audienceMode === 'custom' ? { invited_user_ids: invitedUserIds } : {})
				}
			});
			setLoading(false);
			onPosted?.(created);
		} catch (e) {
			setError(e.message);
			setLoading(false);
		}
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
			style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
			role="presentation"
		>
			<div
				ref={modalRef}
				className="relative flex flex-col w-full min-w-0 max-w-xl bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border text-gray-800 dark:text-dark-text rounded-lg overflow-hidden shadow-xl"
				style={{ maxHeight: '90vh' }}
				role="dialog"
				aria-modal="true"
				aria-labelledby="post-community-challenge-title"
			>
				<div className="relative shrink-0 px-4 sm:px-6 pt-5 pb-4 pr-12 sm:pr-14 border-b border-gray-200 dark:border-dark-border">
					<h3
						id="post-community-challenge-title"
						className="text-xl font-bold text-gray-900 dark:text-dark-text"
					>
						Post community challenge
					</h3>
					<button
						type="button"
						className="absolute top-3 right-2 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition duration-300"
						onClick={onClose}
						aria-label="Close"
					>
						<svg
							className="h-6 w-6 text-gray-500 dark:text-gray-300"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div className="flex flex-col flex-1 min-h-0 overflow-hidden">
					<div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto min-h-0 px-4 sm:px-6 py-4 space-y-6">
						<PostCommunityChallengeConfigurator
							mode={mode}
							onModeChange={setMode}
							activityId={activityId}
							onActivityIdChange={setActivityId}
							customActivityText={customActivityText}
							onCustomActivityTextChange={setCustomActivityText}
							mechanic={mechanic}
							onMechanicChange={setMechanic}
							unitId={unitId}
							onUnitIdChange={setUnitId}
							customUnitText={customUnitText}
							onCustomUnitTextChange={setCustomUnitText}
							featuredKey={featuredKey}
							onFeaturedKeyChange={setFeaturedKey}
							templateKey={templateKey}
							durationDays={durationDays}
							onDurationDaysChange={setDurationDays}
							startsOn={startsOn}
							onStartsOnChange={setStartsOn}
							endsOn={endsOn}
							title={title}
							onTitleChange={setTitle}
							templateCatalog={templateCatalog}
						/>

						<label className="block">
							<span className={fieldLabelClass}>Description (optional)</span>
							<textarea
								className={`${inputClass} mt-2 min-h-[80px]`}
								maxLength={500}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Add context for your network — rules, goals, or encouragement"
							/>
						</label>

						{networkAccounts.length > 0 && (
							<CommunityAudiencePicker
								accounts={networkAccounts}
								audienceMode={audienceMode}
								invitedUserIds={invitedUserIds}
								onAudienceModeChange={setAudienceMode}
								onInvitedUserIdsChange={setInvitedUserIds}
							/>
						)}

						<CommunityChallengePostSummary
							mode={mode}
							creatorName={creatorName}
							title={previewTitle}
							startsOn={startsOn}
							endsOn={endsOn}
							description={description}
							activityId={activityId}
							customActivityText={customActivityText}
							mechanic={mechanic}
							unitId={unitId}
							customUnitText={customUnitText}
							featuredKey={featuredKey}
							templateCatalog={templateCatalog}
							accounts={networkAccounts}
							audienceMode={audienceMode}
							invitedUserIds={invitedUserIds}
						/>
					</div>

					<div className="shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-dark-border space-y-3">
						{error && (
							<p className="text-sm text-red-600 dark:text-red-400" role="alert">
								{error}
							</p>
						)}
						<div className="flex flex-wrap justify-end gap-2">
							<button
								type="button"
								onClick={onClose}
								disabled={loading}
								className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm font-medium"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handlePostClick}
								disabled={loading}
								className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-semibold disabled:opacity-50"
							>
								{loading ? 'Posting…' : 'Post challenge'}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
